import { API_BASE_URL, getAuthToken } from '@/lib/api/client';
import { buildWebMarketingAttributionTouch, type WebMarketingAttributionTouch } from './attribution';
import { useAuthStore } from '@/stores/authStore';

const VISITOR_KEY = 'dobidobi.acquisition.browser.v2';
const VISIT_KEY = 'dobidobi.acquisition.session.v2';
const VISITOR_TTL = 90 * 86400000;
const SESSION_IDLE = 30 * 60000;
type Visit = {
  id: string; claim: string; visitor_id: string; lastActive: number;
  source: string; medium: string; landing_path: string; campaign: string;
  content: string; referrer_host: string; source_type: string;
  recorded?: boolean; boundUser?: string;
  touchKey?: string;
};
let memoryBrowser: { id: string; expires: number } | null = null;
let memoryVisit: Visit | null = null;
let queue: Promise<void> = Promise.resolve();

/** Possession proof only; the API resolves saved dimensions, never trusts supplied ones. */
export function getAcquisitionVisitClaim(): { id: string; claim: string } | undefined {
  if (typeof window === 'undefined') return undefined;
  let visit = memoryVisit;
  try { visit = read<Visit>(window.sessionStorage, VISIT_KEY) || visit; } catch { /* Restricted storage. */ }
  return typeof visit?.id === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(visit.id)
    && typeof visit.claim === 'string' && /^[A-Za-z0-9_-]{32,128}$/.test(visit.claim)
    ? { id: visit.id, claim: visit.claim } : undefined;
}

function read<T>(storage: Storage, key: string): T | null {
  try { return JSON.parse(storage.getItem(key) || 'null') as T | null; } catch { return null; }
}
function saveVisit(visit: Visit): void {
  memoryVisit = visit;
  try { window.sessionStorage.setItem(VISIT_KEY, JSON.stringify(visit)); } catch { /* Memory works without storage. */ }
}
function browserId(now: number): string {
  let stored = memoryBrowser;
  try { stored = read<typeof memoryBrowser>(window.localStorage, VISITOR_KEY) || stored; } catch { /* Restricted storage. */ }
  if (!stored || typeof stored.id !== 'string' || stored.expires <= now) {
    stored = { id: crypto.randomUUID(), expires: now + VISITOR_TTL };
  }
  memoryBrowser = stored;
  try { window.localStorage.setItem(VISITOR_KEY, JSON.stringify(stored)); } catch { /* No fingerprint fallback. */ }
  return stored.id;
}

function dimensions(touch: WebMarketingAttributionTouch | null, referrer: string | null, path: string) {
  let host = '';
  try { host = referrer ? new URL(referrer).hostname.toLowerCase() : ''; } catch { /* Invalid referrer. */ }
  const internal = host === location.hostname || host === 'dobidobi.ai' || host.endsWith('.dobidobi.ai');
  if (internal) host = '';
  const paid = touch?.clickIds || {};
  const paidSource = paid.ttclid ? 'tiktok' : paid.twclid ? 'x' : paid.fbclid ? 'facebook'
    : paid.gclid || paid.gbraid || paid.wbraid ? 'google' : paid.msclkid ? 'bing' : paid.li_fat_id ? 'linkedin' : '';
  return {
    source: (touch?.utm.utm_source || touch?.source || paidSource || host || 'direct').slice(0, 64),
    medium: (touch?.medium || touch?.utm.utm_medium || '').slice(0, 96),
    landing_path: path.slice(0, 1024),
    campaign: (touch?.utm.utm_campaign || '').slice(0, 160),
    content: (touch?.utm.utm_content || touch?.ref || '').slice(0, 160),
    referrer_host: host.slice(0, 255), source_type: touch?.sourceType || (host ? 'external_referrer' : 'direct'),
  };
}

// All routes are observed, including public pages before authentication. Mutations
// are serialized; HTTP + envelope acknowledgment alone marks a fact as persisted.
export function captureAcquisition(userId?: string, input?: {
  touch: WebMarketingAttributionTouch | null; referrer: string | null; path: string; entry: boolean;
}): Promise<void> {
  const captured = input || {
    touch: buildWebMarketingAttributionTouch(new URLSearchParams(location.search), location.pathname, null),
    referrer: null, path: location.pathname, entry: false,
  };
  const work = async () => {
    try {
      const now = Date.now();
      const visitor = browserId(now);
      let visit = memoryVisit;
      try { visit = read<Visit>(window.sessionStorage, VISIT_KEY) || visit; } catch { /* Restricted storage. */ }
      const incoming = dimensions(captured.touch, captured.referrer, captured.path);
      const touchKey = captured.touch ? JSON.stringify([captured.touch.utm, captured.touch.clickIds, captured.touch.ref]) : '';
      const newCampaign = Boolean(captured.touch) && visit &&
        (['source', 'medium', 'campaign', 'content'].some(key =>
          incoming[key as keyof typeof incoming] !== visit![key as keyof Visit])
          || Boolean(visit.touchKey && touchKey !== visit.touchKey));
      const externalEntry = captured.entry && Boolean(incoming.referrer_host);
      if (!visit?.id || !visit.claim || visit.visitor_id !== visitor || !Number.isFinite(visit.lastActive)
        || now - visit.lastActive >= SESSION_IDLE || externalEntry || newCampaign
        || (userId && visit.boundUser && visit.boundUser !== userId)) {
        visit = { id: crypto.randomUUID(), claim: crypto.randomUUID().replaceAll('-', '') + crypto.randomUUID().replaceAll('-', ''),
          visitor_id: visitor, lastActive: now, ...incoming, touchKey };
      }
      if (touchKey) visit.touchKey = touchKey;
      visit.lastActive = now;
      saveVisit(visit);
      const send = async (path: string, token?: string) => {
        const controller = new AbortController();
        const timeout = window.setTimeout(() => controller.abort(), 10000);
        try {
          const { lastActive, recorded, boundUser, touchKey, ...payload } = visit!;
          const response = await fetch(`${API_BASE_URL}/signals/acquisition/${path}`, {
            method: 'POST', headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
            body: JSON.stringify(payload), keepalive: true, signal: controller.signal,
          });
          return response.ok && (await response.json())?.success === true;
        } finally { window.clearTimeout(timeout); }
      };
      if (!visit.recorded) {
        if (!await send('visit')) return;
        visit.recorded = true;
        saveVisit(visit);
      }
      const auth = useAuthStore.getState();
      const token = getAuthToken();
      if (userId && auth.isAuthenticated && auth.user?.id === userId && !auth.user.isAdmin
        && token && visit.boundUser !== userId && await send('bind', token)) {
        visit.boundUser = userId;
        saveVisit(visit);
      }
    } catch { /* Retain the same fact for retry; tracking never blocks the page. */ }
  };
  queue = queue.then(work, work);
  return queue;
}
